import { CreateTaskDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { TaskEntity } from '@/types/api';

import { getTasksQueryKey } from './get-tasks';

export const createTask = async (
  orgId: string,
  projectId: string,
  task: CreateTaskDto,
): Promise<TaskEntity> => {
  const { data } = await api.post<unknown, ApiResponse<TaskEntity>>(
    `/orgs/${orgId}/projects/${projectId}/tasks`,
    task,
  );

  return data;
};

export interface UseCreateTaskOptions {
  mutationConfig?: MutationConfig<(task: CreateTaskDto) => Promise<TaskEntity>>;
}

export const useCreateTask = ({ mutationConfig }: UseCreateTaskOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds();

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationKey: ['tasks', 'create'],
    onSuccess: (...args) => {
      // Invalidate tasks query for this entity
      void queryClient.invalidateQueries({
        queryKey: getTasksQueryKey(orgId, projectId),
      });
      notifications.success('tasks.create.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('tasks.create.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (task: CreateTaskDto) => {
      if (!orgId || !projectId) {
        throw new Error('Org ID and project ID are required');
      }
      return createTask(orgId, projectId, task);
    },
  });
};
